import { lazy } from "react";

import { RoutePathBuilder } from "@/common/builders/routePath";
import AuthorizedRoute from "@/common/components/Auth/AuthorizedRoute";
import { Loadable } from "@/common/components/Loadable";
import { ROUTE_PATH } from "@/common/constants/routing";
import { AppPermission } from "@/core/api/generated/v1";

const AssetSubscriptionPlanCreateUpdatePage = Loadable(
  lazy(
    () =>
      import("../../MainAppView/Products/AssetSubscriptionPlans/AssetSubscriptionPlanCreateUpdatePage"),
  ),
);
const AssetSubscriptionPlanViewPage = Loadable(
  lazy(
    () => import("../../MainAppView/Products/AssetSubscriptionPlans/AssetSubscriptionPlanViewPage"),
  ),
);
const AssetSubscriptionPlansPage = Loadable(
  lazy(
    () => import("../../MainAppView/Products/AssetSubscriptionPlans/AssetSubscriptionPlansPage"),
  ),
);
const AssetSubscriptionCreateUpdatePage = Loadable(
  lazy(
    () => import("../../MainAppView/Products/AssetSubscriptions/AssetSubscriptionCreateUpdatePage"),
  ),
);
const AssetSubscriptionViewPage = Loadable(
  lazy(() => import("../../MainAppView/Products/AssetSubscriptions/AssetSubscriptionViewPage")),
);
const AssetSubscriptionsPage = Loadable(
  lazy(() => import("../../MainAppView/Products/AssetSubscriptions/AssetSubscriptionsPage")),
);
const AssetCreateUpdatePage = Loadable(
  lazy(() => import("../../MainAppView/Products/Assets/AssetCreateUpdatePage")),
);
const AssetViewPage = Loadable(
  lazy(() => import("../../MainAppView/Products/Assets/AssetViewPage")),
);
const AssetsPage = Loadable(lazy(() => import("../../MainAppView/Products/Assets/AssetsPage")));
const PoolItemCreateUpdatePage = Loadable(
  lazy(() => import("../../MainAppView/Products/Pools/PoolItem/PoolItemCreateUpdatePage")),
);
const PoolItemViewPage = Loadable(
  lazy(() => import("../../MainAppView/Products/Pools/PoolItem/PoolItemViewPage")),
);
const PoolItemsPage = Loadable(
  lazy(() => import("../../MainAppView/Products/Pools/PoolItem/PoolItemsPage")),
);
const PoolCreateUpdatePage = Loadable(
  lazy(() => import("../../MainAppView/Products/Pools/Pools/PoolCreateUpdatePage")),
);
const PoolViewPage = Loadable(
  lazy(() => import("../../MainAppView/Products/Pools/Pools/PoolViewPage")),
);
const PoolsPage = Loadable(lazy(() => import("../../MainAppView/Products/Pools/Pools/PoolsPage")));
const ProductLocationCreateUpdatePage = Loadable(
  lazy(() => import("../../MainAppView/Products/ProductLocations/ProductLocationCreateUpdatePage")),
);
const ProductLocationViewPage = Loadable(
  lazy(() => import("../../MainAppView/Products/ProductLocations/ProductLocationViewPage")),
);
const ProductLocationsPage = Loadable(
  lazy(() => import("../../MainAppView/Products/ProductLocations/ProductLocationsPage")),
);

export function ProductRoutes() {
  return (
    <>
      {/* ProductLocations */}
      <AuthorizedRoute
        permissions={[AppPermission.ProductLocationRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.PRODUCT_SALES_LOCATIONS)}
        render={() => <ProductLocationsPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.ProductLocationRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.PRODUCT_SALES_LOCATION_VIEW())}
        render={() => <ProductLocationViewPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.ProductLocationManage, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.PRODUCT_SALES_LOCATION_CREATE)}
        render={() => <ProductLocationCreateUpdatePage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.ProductLocationManage, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.PRODUCT_SALES_LOCATION_EDIT())}
        render={() => <ProductLocationCreateUpdatePage />}
      />

      {/* Asset */}
      <AuthorizedRoute
        permissions={[AppPermission.AssetRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.ASSETS)}
        render={() => <AssetsPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.AssetRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.ASSET_VIEW())}
        render={() => <AssetViewPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.AssetManage, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.ASSET_CREATE)}
        render={() => <AssetCreateUpdatePage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.AssetManage, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.ASSET_EDIT())}
        render={() => <AssetCreateUpdatePage />}
      />

      {/* Pool */}
      <AuthorizedRoute
        permissions={[AppPermission.PoolRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.POOLS)}
        render={() => <PoolsPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.PoolRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.POOL_VIEW())}
        render={() => <PoolViewPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.PoolManage, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.POOL_CREATE)}
        render={() => <PoolCreateUpdatePage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.PoolManage, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.POOL_EDIT())}
        render={() => <PoolCreateUpdatePage />}
      />
      {/* PoolItem */}
      <AuthorizedRoute
        permissions={[AppPermission.PoolRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.POOL_ITEMS())}
        render={() => <PoolItemsPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.PoolRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.POOL_ITEM_VIEW())}
        render={() => <PoolItemViewPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.PoolManage, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.POOL_ITEM_CREATE())}
        render={() => <PoolItemCreateUpdatePage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.PoolManage, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.POOL_ITEM_EDIT())}
        render={() => <PoolItemCreateUpdatePage />}
      />

      {/* AssetSubscriptionPlans */}
      <AuthorizedRoute
        permissions={[AppPermission.AssetSubscriptionPlanRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.ASSET_SUBSCRIPTION_PLANS)}
        render={() => <AssetSubscriptionPlansPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.AssetSubscriptionPlanRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.ASSET_SUBSCRIPTION_PLAN_VIEW())}
        render={() => <AssetSubscriptionPlanViewPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.AssetSubscriptionPlanManage, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.ASSET_SUBSCRIPTION_PLAN_CREATE)}
        render={() => <AssetSubscriptionPlanCreateUpdatePage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.AssetSubscriptionPlanManage, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.ASSET_SUBSCRIPTION_PLAN_EDIT())}
        render={() => <AssetSubscriptionPlanCreateUpdatePage />}
      />

      {/* AssetSubscriptions */}
      <AuthorizedRoute
        permissions={[AppPermission.AssetSubscriptionRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.ASSET_SUBSCRIPTIONS)}
        render={() => <AssetSubscriptionsPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.AssetSubscriptionRead, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.ASSET_SUBSCRIPTION_VIEW())}
        render={() => <AssetSubscriptionViewPage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.AssetSubscriptionManage, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.ASSET_SUBSCRIPTION_CREATE())}
        render={() => <AssetSubscriptionCreateUpdatePage />}
      />
      <AuthorizedRoute
        permissions={[AppPermission.AssetSubscriptionManage, AppPermission.FleetAppAccess]}
        exact
        path={RoutePathBuilder.toDefinition(ROUTE_PATH.ASSET_SUBSCRIPTION_EDIT())}
        render={() => <AssetSubscriptionCreateUpdatePage />}
      />
    </>
  );
}
